import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import type { ApiResponse, ApiError } from '@shared/types';
import { useAuthStore } from '../store/authStore';

// Configurar axios con base URL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true, // Para cookies httpOnly
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * ¿Es esta petición el guardado del resultado de una partida?
 *
 * A estas no se las echa al login, porque eso sacaría al jugador de la pantalla
 * de victoria o al paciente de una sesión terapéutica. Pero tampoco pueden
 * fallar en silencio: antes se perdía la partida entera sin decir nada.
 */
function esGuardadoDePartida(config: AxiosError['config']): boolean {
  if (config?.method?.toLowerCase() !== 'post') return false;
  const url = config?.url || '';
  return url.includes('/progress') || /\/sessions\/games\/.+\/result/.test(url);
}

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      if (esGuardadoDePartida(error.config)) {
        // Se avisa en claro, que es lo que faltaba: el jugador decide si
        // vuelve a identificarse, y al menos sabe que su partida no se guardó.
        toast.error('Tu sesión ha caducado: esta partida no se ha guardado. Vuelve a entrar para que se registre.', {
          duration: 10000,
        });
      } else if (!window.location.pathname.startsWith('/login')) {
        // Limpiar la sesión persistida ANTES de redirigir: si no, isAuthenticated
        // sigue en true en localStorage tras el reload, y AuthLayout devuelve
        // inmediatamente al usuario a /juegos en cuanto aterriza en /login,
        // sin ningún aviso de que la sesión había caducado.
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Helper para extraer data de respuestas
export async function apiRequest<T>(
  promise: Promise<any>
): Promise<T> {
  try {
    const response = await promise;
    return response.data.data || response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as ApiError;
      throw new Error(apiError?.message || apiError?.error || 'Error en la petición');
    }
    throw error;
  }
}

export default api;
